import User from "@/types/user"
import logger from "./logger"
import { useAppFlasher } from "@xy-planning-network/trees"

/**
 * SupportWidget
 * The support widget interface defines the set of methods
 * and properties that should be available for any support
 * widget implementation regardless of the vendor used for
 * the underlying implementation.  The supported methods and
 * properties do not need to mirror the vendor exactly, and instead
 * should be expressive of their expected functionality.
 */
export interface SupportWidget {
  /**
   * identifyUser establishes details of the current user with the support widget.
   * This may be used to establish an identity of the user to the support provider.
   * @param user User
   * @returns void
   */
  identifyUser: (user: User) => void
  /**
   * openArticle will pop open the support widget
   * and display the article associated with the id param.
   *
   * @param id string
   * @returns void
   */
  openArticle: (id: string) => void
  /**
   * queueSuggestions will pre-populate the widget
   * with suggested help articles but not open the widget.
   *
   * @param ids string[]
   * @returns void
   */
  queueSuggestions: (ids: string[]) => void

  /**
   * showMessage will pop open the support widget and
   * display the message identified by the id param
   * @param id the message id to show in the overlay
   * @returns void
   */
  showMessage: (id: string) => void
}

/**
 * A widget stub for when the support widget vendor
 * fails to load or is unavailable in the environment
 */
const widgetStub: SupportWidget = {
  identifyUser() {
    console.log(`Triggered support widget stub "identifyUser".`)
  },
  openArticle(id: string) {
    console.log(
      `Triggered support widget stub "openArticle" method with id: ${id}.`
    )
  },
  queueSuggestions(id: string[]) {
    console.log(
      `Triggered support widget stub "queueSuggestions" method for id's: [${id.join(
        ", "
      )}].`
    )
  },
  showMessage(id: string) {
    console.log(
      `Triggered support widget stub "showMessage" method with id: ${id}.`
    )
  },
}

/**
 * HelpScout Beacon API that is expected on the Window.
 */
declare global {
  interface Window {
    Beacon: (action: string, ...args: any[]) => void
  }
}

/**
 * The current widget implementation via the HelpScout Beacon API
 * https://developer.helpscout.com/beacon-2/web/javascript-api/
 *
 * NOTE: the window.Beacon method takes steps to handle errors gracefully.
 * This is nice as it prevents uncaught errors from bubbling up, but it
 * also means we have little way of knowing when errors do occur.
 * As of this initial implementation there does not appear to be a way to
 * listen for error events on window.Beacon.  Additionally, using try/catch will
 * not be useful because they are handling most errors as opposed to throwing errors.
 */
export const widget: SupportWidget = (() => {
  if (!window.Beacon) {
    return widgetStub
  }

  return {
    // wraps Beacon("article")
    // https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconidentify-userobject
    identifyUser(user: User | null) {
      if (user === null) {
        return
      }

      const params = {
        name: user.fullName,
        email: user.email,
        company: "",
      }
      if (typeof user.firm !== "undefined") {
        params.company = user.firm.name
      }
      window.Beacon("identify", params)
    },
    // wraps Beacon("article")
    // https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconarticle
    openArticle(id: string) {
      if (id === "") {
        useAppFlasher.error(
          `Whoops! We don't have an article for this topic at the help desk, please reach out to <a class="xy-link" href="mailto:membership@xyplanningnetwork.com">membership@xyplanningnetwork.com</a> if you need additional information.`
        )

        logger.report(
          new Error(
            `Support widget article ID cannot be an empty string when opening an article.`
          )
        )

        return
      }

      window.Beacon("article", id, { type: "modal" })
    },
    // wraps Beacon("suggest")
    // https://developer.helpscout.com/beacon-2/web/javascript-api/#beaconsuggest
    queueSuggestions(ids: string[]) {
      // ensure at least one non-empty string exists
      if (ids.reduce((acc, id) => acc + id, "") === "") {
        logger.report(
          new Error(`Support widget suggestions cannot be empty strings.`)
        )

        return
      }

      window.Beacon("suggest", ids)
    },

    // wraps Beacon("show-message")
    // https://developer.helpscout.com/beacon-2/web/javascript-api/#beacon-show-message
    showMessage(id: string) {
      if (id === "") {
        useAppFlasher.error(
          `Whoops! Something unexpected happened.  Please reach out to <a class="xy-link" href="mailto:membership@xyplanningnetwork.com">membership@xyplanningnetwork.com</a> if you need additional information.`
        )

        logger.report(
          new Error(
            `Support widget message ID cannot be an empty string when showing a message.`
          )
        )

        return
      }

      window.Beacon("show-message", id, { delay: 50, force: true })
    },
  }
})()

export default widget
